import i18next, { type TFunction } from 'i18next';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import { formatMinor } from '@/routes/disruptions/care-desk/care-desk-model';
import type { Reservation } from '@/lib/booking/types';
import type { CareCardRow, DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import type { TripView } from '@/lib/transport/types';

// What a voucher SAYS, with no opinion about what it is printed on.
//
// There are two renderers now — the A4/Letter document and the ticket-stock
// coupon — and the one thing that must never differ between them is the content:
// the same passenger can be handed the coupon at the desk and the sheet at the
// gate, and if the two disagree about the pick-up time or the balance on the
// card, the paper has stopped being evidence. So every fact a sheet states is
// derived here, once, and both renderers read it.
//
// The rules that live here and not in a component: never print a balance the
// wallet did not report, never invent a time for a ride that has none, and never
// emit a step for a thing the passenger does not have.
//
// i18n: this module is not a component, so it holds translation KEYS, never
// language. Every builder takes the translator as its LAST argument and defaults
// to `sheetT` — a namespace-bound, language-free `getFixedT`, so a caller with no
// React context (the coupon model, a test) still resolves against whatever
// language the console is showing. Components pass their own `t` where they have
// one: its identity changes on `languageChanged`, which is what redraws a
// memoised section in the new language.
//
// PAPER IS NOT THE CONSOLE. These strings are read by a passenger or a hotel
// front desk, not by an operator — see `docs/ui/frontend-conventions.md` on
// register. The sheet still renders in the CONSOLE's active language, which is a
// real product gap (a Spanish-speaking passenger served by an English console
// gets English paper); fixing it means resolving these keys against a passenger
// language carried on the group, and is deliberately out of scope here.

/** The translator every builder in this module resolves against. */
export type SheetT = TFunction<'pnrActions'>;

/** Resolves a `pnrActions` key against the console's live language. */
export const sheetT: SheetT = i18next.getFixedT(null, 'pnrActions');

/**
 * The four documents an operator can put on paper.
 *
 * Three of them answer one question each; `travel-pack` is the whole night —
 * flight, hotel, the money on the card, the ground transport and the directions
 * — because a re-accommodated passenger is handed all of them at once and none
 * of the single sheets says what to do after the one thing it covers. The single
 * sheets stay: reprinting just the hotel voucher for a front desk is still a
 * real need.
 */
export type PnrPrintKind =
  | 'travel-pack'
  | 'flight-voucher'
  | 'hotel-voucher'
  | 'hotel-directions';

/** How each sheet titles itself, on screen and on paper — as `pnrActions` keys. */
export const SHEET_TITLE_KEY: Record<PnrPrintKind, string> = {
  'travel-pack': 'sheet.title.travelPack',
  'flight-voucher': 'sheet.title.flightVoucher',
  'hotel-voucher': 'sheet.title.hotelVoucher',
  'hotel-directions': 'sheet.title.hotelDirections',
};

/** Reservation states that mean "there is no live hotel booking". */
export const DEAD_RESERVATION = ['CANCELLED', 'RELEASED', 'CANCEL_FAILED'];

export function fmtDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * When a ride happens, in the passenger's terms.
 *
 * A trip with a `scheduledAt` has a real pickup time and prints it in the
 * STATION's local time — a passenger reads the clock on the terminal wall, not
 * UTC and not the operator's browser timezone. A trip without one was dispatched
 * for "now", so the honest sentence is about the driver, not about a clock.
 */
export function tripWhen(
  trip: TripView,
  station: string | undefined,
  t: SheetT = sheetT,
): string {
  if (trip.scheduledAt) {
    return t('sheet.trip.pickUpAt', { when: formatFlightDateTime(trip.scheduledAt, station) });
  }
  return trip.status === 'DISPATCHED' || trip.status === 'IN_TRANSIT'
    ? t('sheet.trip.vehicleOnWay')
    : t('sheet.trip.vehicleRequested');
}

/**
 * The party, as one line of paper.
 *
 * A voucher is checked against documents, so it names the people it covers — in
 * full, never masked (see `use-print-passengers.ts` for why, and for the audited
 * road the names come down). `max` is a paper limit, not a privacy one: a coupon
 * is 82 mm tall and a tour group of thirty does not fit, so the overflow is
 * counted rather than truncated mid-name.
 */
export function passengerListLabel(
  names: readonly string[],
  max: number,
  t: SheetT = sheetT,
): string {
  const present = names.filter((n) => n && n.trim().length > 0);
  if (!present.length) return '—';
  const shown = present.slice(0, max);
  const extra = present.length - shown.length;
  return extra > 0
    ? `${shown.join(' · ')} ${t('sheet.passengerOverflow', { count: extra })}`
    : shown.join(' · ');
}

/** `MASTERCARD` → `Mastercard`. The issuer shouts; a passenger's sheet does not. */
export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export interface CareMoney {
  currency: string;
  /** What was LOADED — face value plus settled top-ups. Always printable. */
  loadedLabel: string;
  /** What the issuer says is LEFT, or the reason there is no number. */
  availableLabel: string | undefined;
  /** The card as the passenger can identify it in their hand. */
  cardLabel: string | undefined;
}

/**
 * The money, as the passenger has to use it.
 *
 * `faceValueMinor + toppedUpMinor` is what was LOADED; `availableMinor` is what
 * the issuer says is left. When the wallet was not read (`known.wallet === false`
 * or the board itself failed) this says the balance was not available — a printed
 * "$0.00" on a card that actually has money on it is worse than no number at
 * all, because the passenger stops using it.
 */
export function careMoney(
  card: CareCardRow | undefined,
  entitlement: DisruptionPnrGroupRow['mealVoucher'],
  balancesKnown: boolean,
  t: SheetT = sheetT,
): CareMoney {
  const currency = card?.currency ?? entitlement?.currency ?? 'USD';
  const loadedMinor = card ? card.faceValueMinor + card.toppedUpMinor : undefined;
  const loadedLabel =
    typeof loadedMinor === 'number'
      ? formatMinor(loadedMinor, currency)
      : typeof entitlement?.amountUsd === 'number'
        ? formatMinor(Math.round(entitlement.amountUsd * 100), currency)
        : '—';
  const availableLabel =
    card && balancesKnown && typeof card.availableMinor === 'number'
      ? formatMinor(card.availableMinor, currency)
      : card
        ? t('sheet.care.balanceUnavailable')
        : undefined;
  const cardLabel = card?.last4
    ? `${card.network ? titleCase(card.network) : t('sheet.care.genericNetwork')} ···· ${card.last4}`
    : undefined;
  return { currency, loadedLabel, availableLabel, cardLabel };
}

/**
 * The numbered "what happens next", built ONLY from facts that exist.
 *
 * This is the half a single-purpose voucher never had: each one tells the
 * passenger about one thing and then stops, leaving them to work out the order
 * themselves at midnight in an airport. Every step below is conditional on its
 * own fact — no hotel, no hotel step; no card, no card step — so the list is
 * short and true rather than a generic script with holes in it.
 */
export function buildNextSteps(
  {
    group,
    reservation,
    card,
    hasCareMoney,
    trips,
  }: {
    group: DisruptionPnrGroupRow;
    reservation: Reservation | undefined;
    card: CareCardRow | undefined;
    hasCareMoney: boolean;
    trips: readonly TripView[];
  },
  t: SheetT = sheetT,
): string[] {
  // Outbound = the ride away from the airport: to the hotel, or home for a stay-home passenger.
  const outbound = trips.find((t) => t.leg === 'AIRPORT_TO_HOTEL' || t.leg === 'AIRPORT_TO_HOME');
  const inbound = trips.find((t) => t.leg === 'HOTEL_TO_AIRPORT');
  const station = group.overnightStation ?? group.destination;
  const flight = group.assignedFlight;
  const hasFlight = !!flight || !!group.ticketNumber;

  const steps: string[] = [];
  if (reservation) {
    steps.push(
      outbound
        ? t('sheet.steps.withRide', {
            station,
            when: tripWhen(outbound, station, t),
            hotel: reservation.hotelName ?? t('sheet.steps.yourHotel'),
          })
        : t('sheet.steps.noRide', { hotel: reservation.hotelName ?? t('sheet.steps.yourHotel') }),
    );
    steps.push(
      t('sheet.steps.checkInHotel', {
        hotel: reservation.hotelName ?? t('sheet.steps.theHotel'),
      }),
    );
  }
  if (hasCareMoney) {
    steps.push(
      card?.last4
        ? t('sheet.steps.payWithCard', { last4: card.last4 })
        : t('sheet.steps.askForCard'),
    );
  }
  if (inbound) {
    steps.push(t('sheet.steps.returnRide', { when: tripWhen(inbound, station, t) }));
  } else if (reservation && hasFlight) {
    steps.push(t('sheet.steps.arrangeReturn'));
  }
  if (hasFlight) {
    const origin = flight?.origin ?? group.origin;
    steps.push(
      t('sheet.steps.checkInFlight', {
        flight: flight?.number ?? group.flightNumber,
        origin,
        when: formatFlightDateTime(flight?.departureAt ?? group.departureAt, origin),
      }),
    );
  }
  return steps;
}
